import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CircularProgress, Box } from '@mui/material';
import axios from 'axios';
import GlobalLoading from './components/GlobalLoading';

// Dynamic Imports
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard'));
const OAuth2Redirect = lazy(() => import('./pages/OAuth2Redirect'));

function App() {
  const { isAuthenticated, user, token } = useSelector((state) => state.auth);

  useEffect(() => {
    const currentToken = token || localStorage.getItem('token');
    
    if (currentToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const PrivateRoute = ({ children, requiredRole }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }
    
    if (requiredRole && user?.role !== requiredRole) {
      const redirectPath = user?.role === 'ADMIN' ? '/admin' : '/customer';
      return <Navigate to={redirectPath} replace />;
    }
    
    return children;
  };

  const PublicRoute = ({ children }) => {
    if (isAuthenticated && user) {
      const redirectPath = user.role === 'ADMIN' ? '/admin' : '/customer';
      return <Navigate to={redirectPath} replace />;
    }
    return children;
  };

  const LoadingFallback = () => (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );

  return (
    <Router>
      <GlobalLoading />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />
          
          <Route path="/register" element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } />
          
          <Route path="/forgot-password" element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          } />
          
          <Route path="/reset-password" element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          } />
          
          <Route path="/oauth2/redirect" element={<OAuth2Redirect />} />
          
          <Route path="/admin" element={
            <PrivateRoute requiredRole="ADMIN">
              <AdminDashboard />
            </PrivateRoute>
          } />
          
          {/* ✅ All customer routes handled by CustomerDashboard */}
          <Route path="/customer/*" element={
            <PrivateRoute requiredRole="CUSTOMER">
              <CustomerDashboard />
            </PrivateRoute>
          } />

          <Route path="/" element={
            isAuthenticated && user ? (
              <Navigate to={user.role === 'ADMIN' ? '/admin' : '/customer'} replace />
            ) : (
              <Navigate to="/login" replace />
            )
          } />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;